import React, { useContext } from 'react'
import { Box } from '@mui/material'

import cardIcon from "../../../images/cardIcon.svg"
import skillValidateIcon from "../../../images/skillValidateIcon.svg"
import sablierIcon from "../../../images/sablierIcon.svg"

import LanguageContext from "../../../contexts/LanguageContext"

/* ⛔ 04/10/2026 — LIGNE DE RESULTAT DE LA FIN DE MODULE (et de « Mes
   certificats », qui l'emploie aussi), D'APRES LA PLANCHE 15.
   Desktop 4:819, Tablet 4:1098 : une rangee de 93 px (marges 15 / 24), le nom a
   gauche, a droite le seuil, la jauge et le pourcentage. Mobile 4:1328 : une
   carte en colonne (marges 20, ecart 15), le seuil SOUS le nom, la jauge en
   pleine largeur dessous.
   ⛔ LA COLONNE JUSQU'A 780 PX D'ECRAN, PAS SEULEMENT EN MOBILE (relecture du
   04/10). Calee sur le palier, la rangee gardait sa jauge et son seuil entiers
   sur une tablette etroite et ne laissait plus RIEN au nom (carte de 500 px :
   0 px de nom ; « Mes certificats » sur un ecran de 600). Sous 780 px d'ecran
   la carte prend donc la colonne du Mobile ; au-dessus, il lui reste au moins
   680 px (fin de module : 40 de marge de chaque cote ; « Mes certificats » :
   50). Le palier ne decide que des cotes de la rangee : Tablet en `sm`, Desktop
   en `md` et `lg` (le palier md suit le Desktop, arbitrage du 22/09).
   ⚠️ Pas de requete de CONTENEUR (`@container`), qui aurait suivi la carte
   elle-meme : le banc de rendu (jsdom) la supprime, et la colonne n'y aurait
   jamais ete mesuree.
   LA JAUGE EST FAITE DE SEGMENTS, comme celle du Diagnostic : 20, le score se
   lit en vingtiemes (« 20 segments partout », arbitrages du 22/09). La planche
   en dessine 20 en Desktop (10 x 40, ecart 8 : 352 px), mais 15 en Tablet
   (204 px) et 19 en Mobile (260 px) ; le code en pose 20 partout, dans la
   largeur de chaque planche, l'ecart ramene a 4 pour les loger. */
const NOIR = "#000000", GRIS = "#EFEFEF"
const SEGMENTS = 20
const ETROITE = "@media (max-width: 779px)"

/* plancher d'une ligne, pour la liste qui la contient (FinDeFormation) : la
   carte en colonne fait 151 px (4:1328), la rangee 93 (4:819) */
export const HAUTEUR_LIGNE_RESULTAT = ["151px", "93px", "93px", "93px"]

/* un nombre, ou un nombre ecrit en texte (« 72 ») ; vide, `null` ou illisible :
   inconnu (relecture securite du 04/10 : l'ancien `>= 0` acceptait « 72 ») */
const enNombre = (v) => (v === null || v === undefined || (typeof v === "string" && v.trim() === "") ? NaN : Number(v))
const seuilConnu = (seuil) => Number.isFinite(enNombre(seuil)) && enNombre(seuil) >= 0
const enSegments = (v) => Math.max(0, Math.min(SEGMENTS, Math.ceil((v / 100) * SEGMENTS)))

/* ⛔ PD-3 : LA COULEUR SUIT LE SEUIL DE CERTIFICATION ET LUI SEUL. Noir jusqu'au
   seuil, jaune du seuil au score, gris au-dela.
   - Un segment est plein des que le score l'entame (arrondi au-dessus) : 87 %
     remplit 18 segments, comme la planche (14 noirs, 4 jaunes, 2 gris :
     4:831 a 4:850). L'arrondi au plus proche en donnait 17.
   - Un score AU-DESSUS du seuil garde au moins un segment jaune : sans cela,
     72 pour un seuil de 68 tombait dans le meme segment et la jauge disait
     « non valide » a cote de la coche « validee » (relecture du 04/10).
   - Seuil inconnu (absent, vide, `null`) : tout le plein en noir, sans repere.
     `null >= 0` est VRAI en JavaScript : un seuil vide passait pour 0 et
     peignait toute la jauge en jaune. */
export const couleursSegments = (resultBrut, seuilBrut) => {
  const result = enNombre(resultBrut), seuil = enNombre(seuilBrut)
  const avecSeuil = seuilConnu(seuil)
  const noirs = avecSeuil ? enSegments(seuil) : SEGMENTS
  let pleins = Number.isFinite(result) ? enSegments(result) : 0
  if (avecSeuil && result > seuil && pleins <= noirs) pleins = Math.min(SEGMENTS, noirs + 1)
  return Array.from({ length: SEGMENTS }, (_, s) => (s >= pleins ? "vide" : s < noirs ? "noir" : "jaune"))
}
/* le repere tombe sur la FRONTIERE noir / jaune, apres le dernier segment noir
   (pose au pourcentage exact, il tombait au milieu d'un segment) */
export const segmentsAvantLeSeuil = (seuil) => (seuilConnu(seuil) ? enSegments(enNombre(seuil)) : null)

const Jauge = ({ result, seuil, seuilColor }) => {
  const couleurs = couleursSegments(result, seuil)
  const frontiere = segmentsAvantLeSeuil(seuil)
  return (
    <Box sx={{
      position:"relative", display:"flex", alignItems:"center",
      /* ecart entre segments et largeur du repere, en variables : les segments
         et le repere s'en deduisent */
      "--ecart":["4px", "4px", "8px", "8px"],
      "--repere":["2px", "2px", "3px", "3px"],
      gap:"var(--ecart)",
      /* 352 en Desktop (4:830), 204 en Tablet (4:1109) ; en colonne, toute la
         place qui reste (4:1343) */
      width:["204px", "204px", "352px", "352px"],
      flex:"none",
      height:["35px", "35px", "40px", "40px"],
      [ETROITE]:{
        "--ecart":"4px", "--repere":"2px",
        width:"auto", flex:"1 1 auto", height:"35px",
        /* la place du repere, qui deborde de 5 px en haut et 6 en bas
           (4:1341 a 471, jauge a 476, repere jusqu'a 517) */
        marginTop:"5px", marginBottom:"6px",
      },
    }}>
      {couleurs.map((c, s) => (
        <Box key={s} sx={{
          /* largeur CALCULEE et non `flex: 1` : avec `flex`, le filet des
             segments vides s'ajoutait a leur part (11,7 px au lieu de 10) */
          flex:"none", height:"100%", boxSizing:"border-box",
          width:`calc((100% - ${SEGMENTS - 1} * var(--ecart)) / ${SEGMENTS})`,
          background: c === "vide" ? GRIS : c === "noir" ? NOIR : seuilColor,
          /* filet #EFEFEF sur les segments vides, de leur couleur (4:850) */
          border: c === "vide" ? `1px solid ${GRIS}` : "none",
        }} />
      ))}
      {frontiere !== null &&
        <Box sx={{
          position:"absolute",
          left:`calc(${frontiere} * (100% + var(--ecart)) / ${SEGMENTS} - var(--ecart) / 2 - var(--repere) / 2)`,
          /* deborde de 6 px en haut (10 en bas) en Desktop, 5 en Tablet et en
             colonne : 4:851 a 286 sur une jauge a 292, 4:1125, 4:1363 */
          top:["-5px", "-5px", "-6px", "-6px"],
          width:"var(--repere)",
          height:["46px", "46px", "56px", "56px"],
          background:seuilColor,
          [ETROITE]:{ top:"-5px", height:"46px" },
        }} />
      }
    </Box>
  )
}

const SkillResultCard = ({ name, result, seuil, validSkill, seuilColor = "#FED64A" }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)
  const libelleSeuil = languageValues?.formation?.seuil?.[actLanguage]
  /* deux-points a la francaise (espace insecable), pas en anglais */
  const deuxPoints = actLanguage === "en" ? ":" : " :"
  const fait = Number.isFinite(enNombre(result)) && enNombre(result) >= 0
  const avecSeuil = seuilConnu(seuil)
  /* 13 / 18,2 en encre a 70 %, le pourcentage 16 / 18,4 (4:854, 4:853) ;
     14 px sur une ligne en colonne (4:1339, 4:1340) */
  const encreSeuil = "rgba(10,10,11,0.7)"

  return (
    <Box sx={{
      flexShrink:0,
      background:"white",
      border:"1px solid #E7E7E7",
      boxSizing:"border-box",
      /* bordure interieure dans la planche : marges 15 / 24 (20 en colonne)
         moins le filet */
      padding:"14px 23px",
      minHeight:"93px",
      display:"flex",
      flexDirection:"row",
      justifyContent:"space-between",
      alignItems:"center",
      gap:["15px", "15px", "20px", "20px"],
      overflow:"hidden",
      [ETROITE]:{ padding:"19px", minHeight:"auto", flexDirection:"column", alignItems:"stretch", gap:"15px" },
    }}>
      <Box sx={{ display:"flex", alignItems:"center", gap:"15px", minWidth:0 }}>
        <Box sx={{ width:"50px", minWidth:"50px", display:"block", overflow:"hidden", aspectRatio:1 }}>
          <img src={cardIcon} alt="" style={{width:"100%", display:"block"}}/>
        </Box>
        <Box sx={{ display:"flex", flexDirection:"column", gap:"10px", minWidth:0 }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:["11px", "11px", "15px", "15px"], minWidth:0, [ETROITE]:{ gap:"8px" } }}>
            <Box sx={{
              whiteSpace:"nowrap",
              textOverflow:"ellipsis",
              overflow:"hidden",
              /* 22 px (20 en Tablet et en colonne), encre #1A1925, -2 %, rogne
                 comme la planche (4:827, 4:1106, 4:1337 ; en colonne, nom +
                 seuil font 32 px, 4:1336).
                 ⚠️ Rogne ET `overflow: hidden` (points de suspension) coupaient
                 le bas des lettres (« logique », photos du 04/10) et couperaient
                 les accents des capitales : une reserve de 0,3 em en haut et en
                 bas, rendue par une marge negative egale, garde la mise en page
                 et laisse peindre les lettres (mesure : « É » depasse de 3,4 px,
                 « g » descend de 4,5, pour 6 de reserve). */
              color:"text.ink",
              fontSize:["20px", "20px", "22px", "22px"],
              lineHeight:"36px",
              letterSpacing:"-0.02em",
              textBox:"trim-both cap alphabetic",
              padding:"0.3em 0",
              margin:"-0.3em 0",
              [ETROITE]:{ fontSize:"20px" },
              /* ⛔ PARTIE 2, POINT 61 de la fiche V2. C'etait un
              `textTransform: capitalize` : une majuscule a
              CHAQUE mot, qui fabriquait des « Regulation
              Emotionnelle » que personne n'avait ecrits. La
              casse vient du contenu ; seule la premiere lettre
              est forcee, pour les anciennes saisies en
              minuscules. */
              "&::first-letter": { textTransform:"uppercase" },
            }}>
              {name}
            </Box>
            {validSkill &&
              <Box sx={{ width:["14px", "14px", "20px", "20px"], minWidth:["14px", "14px", "20px", "20px"], overflow:"hidden", aspectRatio:1, [ETROITE]:{ width:"14px", minWidth:"14px" } }}>
                <img src={skillValidateIcon} alt="✓" style={{width:"100%"}}/>
              </Box>
            }
          </Box>
          {/* en colonne : le seuil sous le nom, sur une ligne (4:1338) */}
          {fait && avecSeuil &&
            <Box sx={{ display:"none", gap:"5px", color:encreSeuil, fontSize:"14px", lineHeight:1.15, letterSpacing:"-0.005em", whiteSpace:"nowrap",
                       /* rogne sur chaque texte : pose sur la rangee, il ne
                          descend pas jusqu'a eux */
                       "& > *":{ textBox:"trim-both cap alphabetic" },
                       [ETROITE]:{ display:"flex" } }}>
              <Box>{libelleSeuil}{deuxPoints}</Box><Box>{seuil}%</Box>
            </Box>
          }
        </Box>
      </Box>
      {fait ? (
        <Box sx={{
          display:"flex",
          alignItems:"center",
          /* seuil -> jauge -> pourcentage : 20 et 20 en Desktop (4:828), 6 et 10
             en Tablet (4:1107), 12 en colonne (4:1341) */
          gap:["8px", "8px", "20px", "20px"],
          flexShrink:0,
          minWidth:0,
          /* la planche centre le groupe jauge + repere (qui deborde de 6 px en
             haut, 10 en bas) : la jauge y est 3 px plus haut que centree
             (4:828 a 286, jauge a 292). Tablet : la planche la centre (4:1107). */
          position:"relative",
          top:[0, 0, "-3px", "-3px"],
          [ETROITE]:{ gap:"12px", top:0 },
        }}>
          {avecSeuil &&
            <Box sx={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"8px", textAlign:"right", whiteSpace:"nowrap", [ETROITE]:{ display:"none" } }}>
              <Box sx={{ color:encreSeuil, fontSize:"13px", lineHeight:1.4, letterSpacing:"-0.005em", textBox:"trim-both cap alphabetic" }}>{libelleSeuil}</Box>
              <Box sx={{ color:encreSeuil, fontSize:"16px", lineHeight:1.15, letterSpacing:"-0.005em", textBox:"trim-both cap alphabetic" }}>{seuil}%</Box>
            </Box>
          }
          <Jauge result={result} seuil={seuil} seuilColor={seuilColor} />
          <Box sx={{
            /* 26 px (22 en Tablet et en colonne), encre #0A0A0B, rogne : 4:829,
               4:1108, 4:1342 */
            color:"#0A0A0B",
            fontSize:["22px", "22px", "26px", "26px"],
            lineHeight:1.4,
            letterSpacing:"-0.005em",
            textBox:"trim-both cap alphabetic",
            minWidth:["43px", "43px", "51px", "51px"],
            textAlign:"right",
            [ETROITE]:{ fontSize:"22px", minWidth:"43px" },
          }}>
            {result}%
          </Box>
        </Box>
      ) : (
        <Box sx={{ display:"flex", gap:["6px", "8px", "8px", "8px"], alignItems:"center" }}>
          <Box sx={{ width:["10px", "12px", "12px", "14px"], minWidth:["10px", "12px", "12px", "14px"], overflow:"hidden", aspectRatio:"14/17.22" }}>
            <img src={sablierIcon} alt="" style={{width:"100%"}}/>
          </Box>
          <Box sx={{ color:"text.black", fontSize:["13px", "14px", "14px", "16px"] }}>
            {languageValues?.formation?.pending[actLanguage]}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default SkillResultCard
